<script setup>
import BaseTaskHelp from '@/components/base/BaseTaskHelp.vue';
import BaseWorkflowSteps from '@/components/base/BaseWorkflowSteps.vue';
import BaseFormField from '@/components/base/BaseFormField.vue';
import BaseInput from '@/components/base/BaseInput.vue';
import BaseTextarea from '@/components/base/BaseTextarea.vue';
import { ref, computed, onMounted, watch } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useI18n } from 'vue-i18n';
import MdiBarcodeScan from '~icons/mdi/barcode-scan';
import MdiClipboardList from '~icons/mdi/clipboard-list';
import MdiPackageVariant from '~icons/mdi/package-variant';
import MdiAccount from '~icons/mdi/account';
import MdiAccountQuestion from '~icons/mdi/account-question';
import MdiWrench from '~icons/mdi/wrench';
import MdiAccountGroup from '~icons/mdi/account-group';
import SearchField from '@/components/search/SearchField.vue';
import MdiAlertCircle from '~icons/mdi/alert-circle';
import MdiClose from '~icons/mdi/close';
import QuantityModal from '@/components/picking/QuantityModal.vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useStore } from '@/stores/store';
import { useToast } from '@/composables/useToast';
import {
  getSearchResults,
  getItemInstance,
  getBundle,
  getItemInstances,
  getProjects,
  getProject,
  getUserSearchResults,
} from '@/api/api';
import { useOfflineOps } from '@/composables/useOfflineOps.js';
import { recordMaintenanceFlag } from '@/composables/useOpsMetrics.js';

const store  = useStore();
const router = useRouter();
const route = useRoute();
const auth = useAuthStore();
const toast  = useToast();
const { t }  = useI18n();

// ── Phases: 'scan' | 'action' | 'target' | 'resolve' | 'confirm' ──────────
const phase = ref('scan');

// ── Scan input ───────────────────────────────────────────────────────────────
const scanFieldRef = ref(null);
const scanQuery    = ref('');
const scanError    = ref(null);
const scanLoading  = ref(false);
const liveResults  = ref([]);
const liveLoading  = ref(false);

const refocus = () => scanFieldRef.value?.focus();

const mappedLiveResults = computed(() =>
  liveResults.value.map(r => ({
    id: String(r.id),
    label: r.displayName,
    sublabel: r.sub ?? '',
    badge: r.resultType === 'bundle' ? t('entity.bundle') : undefined,
    meta: r,
  }))
);

async function doLiveSearch(val) {
  if (!val || val.trim().length < 2) { liveResults.value = []; return; }
  liveLoading.value = true;
  try {
    const res = await getSearchResults(val.trim());
    const items = [];
    (res.results?.item_metadata ?? []).forEach(r => {
      items.push({ id: r.object_id, resultType: 'item_metadata', displayName: r.display?.name ?? `#${r.object_id}`, sub: r.display?.subtitle || null, raw: { id: r.object_id, item: { name: r.display?.name } } });
    });
    (res.results?.bundle ?? []).forEach(r => {
      const rawSku = (r.display?.subtitle ?? '').replace(/^SKU:\s*/i, '');
      items.push({
        id: r.object_id,
        resultType: 'bundle',
        displayName: r.display?.name ?? t('ui.bundleNumber', { id: r.object_id }),
        sub: r.display?.subtitle || null,
        raw: { id: r.object_id, name: r.display?.name, sku: rawSku }
      });
    });
    liveResults.value = items;
  } catch {
    liveResults.value = [];
  } finally {
    liveLoading.value = false;
  }
}

// ── Bundle checkout modal ────────────────────────────────────────────────────
// (Removed — bundles now go through the unified checkout flow)

const addFromLive = async (result) => {
  liveResults.value = [];
  scanQuery.value   = '';
  scanError.value   = null;

  if (result.resultType === 'bundle') {
    store.addCheckoutBundle({
      id: result.id,
      name: result.displayName,
      sku: result.raw?.sku ?? '',
      itemCount: result.raw?.itemCount ?? 0,
    });
    toast.success(t('checkout.addedBundle', { name: result.displayName }));
    refocus();
    return;
  }

  scanLoading.value = true;
  try {
    let meta = await getItemInstance(result.id).catch(() => null) ?? result.raw;
    if (!store.items.find(x => x.id === meta.id)) store.items.push(meta);
    if (isInList(meta.id)) {
      scanError.value = t('checkout.alreadyInList', { name: meta.item?.name ?? '#' + meta.id });
    } else if (meta.in_downtime) {
      scanError.value = t('ui.downtimeCheckout', { name: meta.item?.name ?? '#' + meta.id, reason: meta.downtime_reason ? ': ' + meta.downtime_reason : '' });
    } else if (meta.item?.item_type === 3 || meta.item?.item_type === 4) {
      // Type 3 (Quantity) and type 4 (Consumable) require a quantity before adding
      scanLoading.value = false;
      openQtyModal(meta);
      return;
    } else if (meta.is_checked_out) {
      scanError.value = t('checkout.alreadyInList', { name: meta.item?.name ?? '#' + meta.id });
    } else {
      store.addCheckoutItem(meta);
      if (meta.is_planned) {
        toast.warning(t('checkout.addedItemPlanned', { name: meta.item?.name ?? '#' + meta.id }));
      } else {
        toast.success(t('checkout.addedItem', { name: meta.item?.name ?? '#' + meta.id }));
      }
    }
  } catch {
    scanError.value = t('checkout.loadItemFailed');
  } finally {
    scanLoading.value = false;
    refocus();
  }
};

onMounted(async () => {
  store.loadCheckoutList();
  if (route.query.project) {
    const match = await getProject(route.query.project).catch(() => null);
    if (match) {
      selectedAction.value = ACTIONS.value.find(a => a.id === (route.query.plan ? 'pick-list' : 'checkout-project'));
      selectedTarget.value = { ...match, label: match.name };
      phase.value = 'target';
    }
  }
  refocus();
  // Enrich items that are missing availability flags (added from other views)
  for (const m of store.checkoutItemList) {
    if (m._isBundle || m.is_checked_out !== undefined) continue;
    try {
      const fresh = await getItemInstance(m.id);
      Object.assign(m, {
        is_checked_out: fresh.is_checked_out,
        is_planned: fresh.is_planned,
        available: fresh.available,
        in_downtime: fresh.in_downtime,
        item: fresh.item ?? m.item,
        serial_number: fresh.serial_number ?? m.serial_number,
      });
    } catch { /* non-critical */ }
  }
});

// ── Checkout list ────────────────────────────────────────────────────────────
const scannedItems = computed(() => store.checkoutItemList);
const isInList     = (id) => scannedItems.value.some(m => m.id == id);

const findMetadata = (q) => {
  const qn = q.trim().toLowerCase();
  return store.items.find(m =>
    String(m.id) === qn ||
    (m.serial_number && m.serial_number.toLowerCase() === qn) ||
    (m.assetTag      && m.assetTag.toLowerCase()      === qn)
  );
};

const handleScan = async (codeParam = '') => {
  const q = (typeof codeParam === 'string' && codeParam ? codeParam : scanQuery.value).trim();
  if (!q) return;

  scanError.value   = null;
  scanLoading.value = true;

  try {
    let meta = findMetadata(q);

    if (!meta) {
      const res = await getSearchResults(q);

      // A scanner submission must resolve to an exact identity. Search results
      // are fuzzy and can contain an unrelated bundle or instance.
      const metas = res.results?.item_metadata ?? [];
      const matchingMeta = metas.find(m =>
        String(m.object_id) === q ||
        (m.display?.name ?? '').toLowerCase() === q.toLowerCase()
      );
      const candidates = matchingMeta ? [matchingMeta] : metas;
      for (const candidate of candidates) {
        const instance = await getItemInstance(candidate.object_id).catch(() => null);
        if (instance && (
          String(instance.id) === q ||
          instance.assetTag?.toLowerCase() === q.toLowerCase() ||
          instance.serial_number?.toLowerCase() === q.toLowerCase()
        )) {
          meta = instance;
          if (!store.items.find(x => x.id === meta.id)) store.items.push(meta);
          break;
        }
      }

      const bundles = res.results?.bundle ?? [];
      const bundleMatch = !meta && bundles.find(b =>
        String(b.object_id) === q ||
        (b.display?.subtitle ?? '').replace(/^SKU:\s*/i, '').toLowerCase() === q.toLowerCase() ||
        (b.display?.name ?? '').toLowerCase() === q.toLowerCase()
      );
      if (bundleMatch) {
        store.addCheckoutBundle({
          id: bundleMatch.object_id,
          name: bundleMatch.display?.name ?? t('ui.bundleNumber', { id: bundleMatch.object_id }),
          sku: (bundleMatch.display?.subtitle ?? '').replace(/^SKU:\s*/i, ''),
          itemCount: 0,
        });
        toast.success(t('checkout.addedBundle', { name: bundleMatch.display?.name ?? bundleMatch.object_id }));
        scanQuery.value   = '';
        scanLoading.value = false;
        refocus();
        return;
      }

    }

    if (!meta) {
      scanError.value = t('checkout.notFound', { code: q });
    } else if (isInList(meta.id)) {
      scanError.value = t('checkout.alreadyInList', { name: meta.item?.name ?? '#' + meta.id });
    } else if (meta.in_downtime) {
      scanError.value = t('ui.downtimeCheckout', { name: meta.item?.name ?? '#' + meta.id, reason: meta.downtime_reason ? ': ' + meta.downtime_reason : '' });
    } else if (meta.item?.item_type === 3 || meta.item?.item_type === 4) {
      scanLoading.value = false;
      scanQuery.value   = '';
      openQtyModal(meta);
      return;
    } else if (meta.is_checked_out) {
      scanError.value = t('checkout.alreadyInList', { name: meta.item?.name ?? '#' + meta.id });
    } else {
      store.addCheckoutItem(meta);
      if (meta.is_planned) {
        toast.warning(t('checkout.addedItemPlanned', { name: meta.item?.name ?? '#' + meta.id }));
      } else {
        toast.success(t('checkout.addedItem', { name: meta.item?.name ?? '#' + meta.id }));
      }
    }
  } catch {
    scanError.value = t('checkout.searchFailed');
  } finally {
    scanQuery.value   = '';
    scanLoading.value = false;
    refocus();
  }
};

const removeItem = (id) => store.removeCheckoutItem(id);
const clearList  = () => { store.checkoutItemList = []; localStorage.removeItem('checkoutList'); };

// ── Quantity modal (Type 3 & 4) ───────────────────────────────────────────────
const qtyTarget    = ref(null);
const qtyStockInfo = ref(null);  // { available_quantity, consumable_net_available } for type 4

const openQtyModal = async (meta) => {
  qtyTarget.value    = meta;
  qtyStockInfo.value = null;
  if (meta.item?.item_type === 4) {
    try {
      const ext = await getItemInstance(meta.id);
      qtyStockInfo.value = {
        available_quantity:       Number(ext.available_quantity ?? 0),
        consumable_net_available: Number(ext.consumable_net_available ?? ext.available_quantity ?? 0),
      };
    } catch { /* show modal anyway */ }
  }
};

const onQtyConfirm = ({ quantity }) => {
  if (!isInList(qtyTarget.value.id)) {
    store.addCheckoutItem({ ...qtyTarget.value, qty: quantity });
    toast.success(t('checkout.addedQtyItem', { qty: quantity, name: qtyTarget.value.item?.name }));
  }
  qtyTarget.value = null;
  refocus();
};

const onQtyCancel = () => {
  qtyTarget.value = null;
  refocus();
};

// ── Action selection ──────────────────────────────────────────────────────────
const ACTIONS = computed(() => [
  {
    id: 'pick-list',
    label: t('checkout.actions.pickList'),
    icon: MdiClipboardList,
    iconClass: 'text-blue-400',
    desc: t('checkout.actions.pickListDesc'),
    target: 'project',
    disabled: false,
  },
  {
    id: 'checkout-project',
    label: t('checkout.actions.checkoutProjectFull'),
    icon: MdiPackageVariant,
    iconClass: 'text-amber-400',
    desc: t('checkout.actions.checkoutProjectFullDesc'),
    target: 'project',
    disabled: false,
  },
  {
    id: 'checkout-person',
    label: t('checkout.actions.checkoutPerson'),
    icon: MdiAccount,
    iconClass: 'text-sky-400',
    desc: t('checkout.actions.checkoutPersonDirectDesc'),
    target: 'person',
    disabled: false,
  },
  {
    id: 'checkout-anon',
    label: t('checkout.actions.checkoutAnon'),
    icon: MdiAccountQuestion,
    iconClass: 'text-violet-400',
    desc: t('checkout.actions.checkoutAnonDirectDesc'),
    target: 'anon',
    disabled: false,
  },
  {
    id: 'checkout-person-project',
    label: t('checkout.actions.checkoutPersonProject'),
    icon: MdiAccountGroup,
    iconClass: 'text-emerald-400',
    desc: t('checkout.actions.checkoutPersonProjectDirectDesc'),
    target: 'person-on-project',
    disabled: false,
  },
  {
    id: 'maintenance',
    label: t('checkout.actions.maintenance'),
    icon: MdiWrench,
    iconClass: 'text-orange-400',
    desc: t('checkout.actions.maintenanceDesc'),
    target: 'maintenance',
    disabled: false,
  },
]);

const selectedAction = ref(null);
const maintenanceNote = ref('');
const maintenanceSubmitting = ref(false);

// ── Two-step state for checkout-person-project ─────────────────────────────
const personOnProjectStep      = ref('project')  // 'project' | 'person'
const selectedProjectForAssign = ref(null)        // { id, label, sub }
const projectFilterTab         = ref('active')    // 'active' | 'upcoming' | 'finished'
const projectTabResults        = ref([])
const projectTabLoading        = ref(false)
// person type toggle for step 2: 'user' | 'anon'
const assignPersonType         = ref('user')

const selectAction = (action) => {
  if (action.disabled) return;
  selectedAction.value = action;
  targetQuery.value    = '';
  targetResults.value  = [];
  selectedTarget.value = null;
  anonUserForm.value   = { full_name: '', email: '', phone: '', organization: '', external_id: '', notes: '' };
  // Reset two-step state
  personOnProjectStep.value      = 'project';
  selectedProjectForAssign.value = null;
  projectFilterTab.value         = 'active';
  projectTabResults.value        = [];
  assignPersonType.value         = 'user';
  maintenanceNote.value = '';
  if (action.id === 'maintenance') {
    phase.value = 'confirm';
    return;
  }
  phase.value = 'target';
  if (action.id === 'checkout-person-project') {
    loadProjectTab('active');
  }
};

const { queuedCartCheckout, queuedDowntime } = useOfflineOps();

async function submitMaintenance() {
  if (maintenanceSubmitting.value) return;
  const metas = scannedItems.value.filter(m => !m._isBundle && m.id);
  if (!metas.length) {
    toast.error(t('checkout.maintenanceNeedsInstances'));
    return;
  }
  maintenanceSubmitting.value = true;
  submitErrors.value = [];
  try {
    for (const m of metas) {
      await queuedDowntime(m.id, true, maintenanceNote.value.trim());
      recordMaintenanceFlag({ itemMetadataId: m.id });
    }
    clearList();
    toast.success(t('checkout.maintenanceDone', { count: metas.length }));
    router.push('/maintenance/schedules');
  } catch (e) {
    const message = e?.data?.detail ?? e?.message ?? t('checkout.operationFailed');
    submitErrors.value = [message];
    toast.error(message);
  } finally {
    maintenanceSubmitting.value = false;
  }
}

// ── Anonymous borrower form ───────────────────────────────────────────────────
const anonUserForm = ref({ full_name: '', email: '', phone: '', organization: '', external_id: '', notes: '' });
const anonUserFormValid = computed(() => anonUserForm.value.full_name.trim().length > 0);

// ── Target selection ──────────────────────────────────────────────────────────
const targetQuery   = ref('');
const targetResults = ref([]);
const targetLoading = ref(false);
const selectedTarget = ref(null);

let targetDebounce = null;

watch(targetQuery, (val) => {
  // Don't clear selectedTarget when the query change was caused by pickTarget
  // setting targetQuery to the selected item's label — that would require a double-click.
  if (selectedTarget.value && val === selectedTarget.value.label) return;
  selectedTarget.value = null;
  clearTimeout(targetDebounce);
  if (selectedAction.value?.id === 'checkout-person-project' && personOnProjectStep.value === 'project') {
    // For the two-step flow, reload tab with search filter (debounced)
    targetDebounce = setTimeout(() => loadProjectTab(projectFilterTab.value), 280);
    return;
  }
  if (val.trim().length < 2) { targetResults.value = []; return; }
  targetDebounce = setTimeout(() => searchTargets(val.trim()), 280);
});

const searchTargets = async (q) => {
  targetLoading.value = true;
  try {
    if (selectedAction.value?.target === 'project') {
      const res = await getProjects({ name__icontains: q, page_size: 20 });
      targetResults.value = (res.results ?? res ?? []).map(p => ({
        id: p.id,
        label: p.name,
        sub: p.customer?.name ?? null,
      }));
    } else {
      const res = await getUserSearchResults(q);
      targetResults.value = (Array.isArray(res) ? res : (res.results ?? [])).map(u => ({
        id: u.id,
          label: u.full_name ?? (`${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.username),
        sub: u.email ?? null,
      }));
    }
  } catch {
    targetResults.value = [];
  } finally {
    targetLoading.value = false;
  }
};

const pickTarget = (t) => {
  selectedTarget.value = t;
  targetResults.value  = [];
  targetQuery.value    = t.label;
};

// Project status values: DRAFT=0, CONFIRMED=1, ACTIVE=5, COMPLETED=7, CANCELLED=8
const PROJECT_TABS = computed(() => [
  { key: 'active',   label: t('checkout.tabs.active'),   status: [1, 5] },
  { key: 'upcoming', label: t('checkout.tabs.upcoming'), status: [0, 1] },
  { key: 'finished', label: t('checkout.tabs.finished'), status: [7, 8] },
]);

const loadProjectTab = async (tab) => {
  projectFilterTab.value  = tab;
  projectTabResults.value = [];
  projectTabLoading.value = true;
  const tabDef = PROJECT_TABS.value.find(t => t.key === tab);
  try {
    const params = { page_size: 50, status: tabDef.status };
    if (targetQuery.value.trim().length >= 2) params.name__icontains = targetQuery.value.trim();
    const res = await getProjects(params);
    projectTabResults.value = (res.results ?? res ?? []).map(p => ({
      id: p.id,
      label: p.name,
      sub: p.customer?.name ?? null,
    }));
  } catch {
    projectTabResults.value = [];
  } finally {
    projectTabLoading.value = false;
  }
};

const pickProjectForAssign = (p) => {
  selectedProjectForAssign.value = p;
  targetQuery.value              = '';
  personOnProjectStep.value      = 'person';
  targetResults.value            = [];
};

// ── Expected check-in (person checkout only) ─────────────────────────────────
const expectedCheckin = ref('');

const QUICK_DATES = computed(() => [
  { key: 'tomorrow',    label: t('checkout.quickDates.tomorrow') },
  { key: 'end-of-week', label: t('checkout.quickDates.endOfWeek') },
  { key: '1-week',      label: t('checkout.quickDates.oneWeek') },
  { key: '1-month',     label: t('checkout.quickDates.oneMonth') },
]);

const _quickDateValue = (preset) => {
  const d   = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  if (preset === 'tomorrow') {
    d.setDate(d.getDate() + 1);
    d.setHours(12, 0, 0, 0);
  } else if (preset === 'end-of-week') {
    const daysToFriday = (5 - d.getDay() + 7) % 7;
    d.setDate(d.getDate() + daysToFriday);
    d.setHours(23, 59, 0, 0);
  } else if (preset === '1-week') {
    d.setDate(d.getDate() + 7);
    d.setHours(12, 0, 0, 0);
  } else if (preset === '1-month') {
    d.setMonth(d.getMonth() + 1);
    d.setHours(12, 0, 0, 0);
  }
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const setQuickDate = (preset) => { expectedCheckin.value = _quickDateValue(preset); };

const activeQuickDate = computed(() => {
  if (!expectedCheckin.value) return null;
  for (const { key } of QUICK_DATES.value) {
    if (_quickDateValue(key) === expectedCheckin.value) return key;
  }
  return null;
});

// ── Resolve phase (instance selection for item-type bundle members) ────────
// Each entry: { bundleId, bundleName, members: [{ bundleItemId, itemId, itemName, quantity, instances: [], selectedInstanceId: null }] }
const resolveGroups = ref([]);
const resolveLoading = ref(false);

const needsResolve = computed(() => resolveGroups.value.some(
  g => g.members.some(m => !m.selectedInstanceId)
));

const allResolved = computed(() => resolveGroups.value.length > 0 && resolveGroups.value.every(
  g => g.members.every(m => m.selectedInstanceId)
));

async function enterResolvePhase() {
  // For pick-list: instance resolution happens at pick time, not planning time.
  // Skip the resolve phase entirely and proceed to confirm.
  if (selectedAction.value.id === 'pick-list') {
    await refreshAvailability();
    phase.value = 'confirm';
    return;
  }

  const bundles = scannedItems.value.filter(m => m._isBundle);
  if (!bundles.length) {
    await refreshAvailability();
    phase.value = 'confirm';
    return;
  }

  resolveLoading.value = true;
  resolveGroups.value = [];

  try {
    const groupsRaw = await Promise.all(bundles.map(async (b) => {
      const bundleData = await getBundle(b.bundleId);
      const items = bundleData.items ?? bundleData.results ?? [];
      // Only include members that reference a generic Item (no specific instance)
      // and only for Multi-Item (2) — Unique (1), Quantity (3) and Consumable (4)
      // have a single instance that can be auto-resolved by the backend.
      const membersNeedingResolve = items.filter(bi => {
        if (!bi.item || bi.item_metadata) return false;
        const itemType = bi.item.item_type ?? bi.item;
        return itemType === 2;
      });
      if (!membersNeedingResolve.length) return null;

      const memberEntries = await Promise.all(membersNeedingResolve.map(async (bi) => {
        const instancesRes = await getItemInstances(bi.item.id ?? bi.item);
        const instanceList = (instancesRes.results ?? instancesRes ?? []).map(inst => {
          const parts = [inst.assetTag, inst.serial_number].filter(Boolean);
          const checkedOut = inst.is_checked_out;
          const planned = inst.is_planned;
          const statusLabel = checkedOut ? t('checkout.checkedOutBadge') : planned ? t('checkout.plannedOnProject') : inst.available ? t('instanceStatus.available') : t('ui.unavailable');
          const isAvailable = !checkedOut && !planned && inst.available;
          return {
            id: inst.id,
            label: parts.length ? parts.join(' · ') : `#${inst.id}`,
            sub: statusLabel,
            available: isAvailable,
          };
        });
        return {
          bundleItemId: bi.id,
          itemId: bi.item.id ?? bi.item,
          itemName: bi.item.name ?? t('ui.itemNumber', { id: bi.item.id ?? bi.item }),
          quantity: bi.quantity ?? 1,
          instances: instanceList,
          selectedInstanceId: null,
          searchQuery: '',
        };
      }));
      return {
        bundleId: b.bundleId,
        bundleName: b.name ?? t('ui.bundleNumber', { id: b.bundleId }),
        members: memberEntries,
      };
    }));
    const groups = groupsRaw.filter(Boolean);

    resolveGroups.value = groups;

    if (!groups.length) {
      // No bundles need resolution — skip to confirm
      await refreshAvailability();
      phase.value = 'confirm';
    } else {
      phase.value = 'resolve';
    }
  } catch (e) {
    toast.error(t('checkout.bundleDetailsFailed'));
    console.error(e);
  } finally {
    resolveLoading.value = false;
  }
}

function selectInstance(groupIdx, memberIdx, instanceId) {
  resolveGroups.value[groupIdx].members[memberIdx].selectedInstanceId = instanceId;
}

// Build instance_overrides for a specific bundle from resolveGroups
function getInstanceOverrides(bundleId) {
  const group = resolveGroups.value.find(g => g.bundleId === bundleId);
  if (!group) return [];
  return group.members
    .filter(m => m.selectedInstanceId)
    .map(m => ({ bundle_item_id: m.bundleItemId, item_metadata_id: m.selectedInstanceId }));
}

// ── Confirm-phase availability warnings ───────────────────────────────────────
const confirmWarnings = ref([]);

async function refreshAvailability() {
  confirmWarnings.value = [];
  const nonBundles = scannedItems.value.filter(m => !m._isBundle);
  const checks = await Promise.allSettled(
    nonBundles.map(async (m) => {
      try {
        const fresh = await getItemInstance(m.id);
        // Update stored flags so the list UI updates too
        m.is_checked_out = fresh.is_checked_out;
        m.is_planned = fresh.is_planned;
        m.available = fresh.available;
        m.in_downtime = fresh.in_downtime;
        const name = m.item?.name ?? `#${m.id}`;
        if (fresh.is_checked_out) return { id: m.id, name, level: 'error', msg: t('ui.alreadyCheckedOutName', { name }) };
        if (fresh.in_downtime) return { id: m.id, name, level: 'error', msg: t('ui.downtimeName', { name }) };
        if (fresh.is_planned) return { id: m.id, name, level: 'warn', msg: t('ui.plannedElsewhere', { name }) };
        if (!fresh.available) return { id: m.id, name, level: 'warn', msg: t('ui.unavailableName', { name }) };
        return null;
      } catch { return null; }
    })
  );
  confirmWarnings.value = checks
    .map(r => r.status === 'fulfilled' ? r.value : null)
    .filter(Boolean);
}

// ── Submit ────────────────────────────────────────────────────────────────────
const submitting = ref(false);
const submitErrors = ref([]);

// Persist warning suppression in localStorage
const suppressCheckoutWarning = ref(localStorage.getItem('suppress_checkout_warning') === '1');
function toggleSuppressWarning() {
  suppressCheckoutWarning.value = !suppressCheckoutWarning.value;
  localStorage.setItem('suppress_checkout_warning', suppressCheckoutWarning.value ? '1' : '0');
}

const handleSubmit = async () => {
  if (submitting.value || !scannedItems.value.length || !selectedAction.value) return;
  const action = selectedAction.value.id;
  const isPersonProject = action === 'checkout-person-project';
  const isAnon = action === 'checkout-anon' || (isPersonProject && assignPersonType.value === 'anon');
  if (isAnon && !anonUserFormValid.value) return;
  if (!isAnon && !selectedTarget.value) return;
  if (isPersonProject && !selectedProjectForAssign.value) return;
  const target = {};
  if (isPersonProject) {
    target.project = selectedProjectForAssign.value.id;
    target.assign_person = true;
  } else if (action === 'pick-list' || action === 'checkout-project') {
    target.project = selectedTarget.value.id;
  }
  if (isAnon) target.anon_user = Object.fromEntries(Object.entries(anonUserForm.value).map(([k,v]) => [k, v.trim()]));
  else if (action === 'checkout-person' || isPersonProject) target.user = selectedTarget.value.id;
  if (expectedCheckin.value) target.expected_checkin = expectedCheckin.value + ':00';
  const lines = scannedItems.value.map(m => ({
    ...target,
    operation: action === 'pick-list' ? 'plan' : 'checkout',
    ...(m._isBundle ? { kind: 'bundle', bundle: m.bundleId, instance_overrides: getInstanceOverrides(m.bundleId) }
      : { kind: 'item', item_metadata: m.id, quantity: m.qty ?? 1 }),
  }));
  const storageKey = 'checkoutCommit:' + auth.currentTenant?.id + ':' + auth.user?.id;
  const fingerprint = JSON.stringify(lines);
  submitting.value = true;
  submitErrors.value = [];
  try {
    let attempt;
    try { attempt = JSON.parse(localStorage.getItem(storageKey)); } catch { /* replace invalid local state */ }
    if (attempt?.fingerprint !== fingerprint) {
      attempt = { fingerprint, key: uuidv4() };
      localStorage.setItem(storageKey, JSON.stringify(attempt));
    }
    await queuedCartCheckout({ key: attempt.key, lines });
    clearList();
    localStorage.removeItem(storageKey);
    toast.success(t(action === 'pick-list' ? 'checkout.addedToPickList' : 'checkout.checkedOut'));
    router.push('/');
  } catch (e) {
    const detail = e?.data?.detail;
    const message = detail?.error ?? e?.data?.error ?? e?.message ?? t('checkout.operationFailed');
    submitErrors.value = [message];
    toast.error(message);
  } finally {
    submitting.value = false;
  }
};

const goBack = () => {
  if (submitting.value) return;
  if (phase.value === 'action') {
    phase.value = 'scan';
  } else if (phase.value === 'target') {
    if (selectedAction.value?.id === 'checkout-person-project' && personOnProjectStep.value === 'person') {
      personOnProjectStep.value = 'project';
      targetQuery.value         = '';
      selectedTarget.value      = null;
    } else {
      phase.value = 'action';
    }
  } else if (phase.value === 'resolve') {
    phase.value = 'target';
  } else {
    // confirm → resolve (if resolve has data) or target
    phase.value = resolveGroups.value.length ? 'resolve' : 'target';
  }
};

const workflowSteps = computed(() => [
  { id: 'scan', label: t('checkout.scanItems') },
  { id: 'action', label: t('checkout.whatToDo') },
  { id: 'target', label: t('ux.chooseRecipient') },
  ...(resolveGroups.value.length ? [{ id: 'resolve', label: t('checkout.selectInstances') }] : []),
  { id: 'confirm', label: t('ux.review') },
]);

const typeLabel = computed(() => ({
  1: t('itemTypes.1'),
  2: t('itemTypes.2'),
  3: t('itemTypes.3'),
  4: t('itemTypes.4'),
}));
</script>

<template>
  <div class="flex flex-col min-h-screen pb-24">

    <!-- Phase header ──────────────────────────────────────────────────── -->
    <div class="sticky top-[92px] z-20 px-4 py-3 flex items-center justify-between gap-3 bg-[var(--color-bg)] border-b border-[var(--color-border)]">
      <div>
        <h1 class="font-bold text-white text-base leading-tight">
          <template v-if="phase === 'scan'">{{ $t('checkout.scanItems') }}</template>
          <template v-else-if="phase === 'action'">{{ $t('checkout.whatToDo') }}</template>
          <template v-else-if="phase === 'target'">
            <template v-if="selectedAction?.id === 'checkout-person-project'">
              {{ personOnProjectStep === 'project' ? $t('checkout.selectProject') : $t('checkout.selectPerson') }}
            </template>
            <template v-else>
              {{ selectedAction?.target === 'project' ? $t('checkout.selectProject') : selectedAction?.target === 'anon' ? $t('checkout.guestDetails') : $t('checkout.selectPerson') }}
            </template>
          </template>
          <template v-else-if="phase === 'resolve'">{{ $t('checkout.selectInstances') }}</template>
          <template v-else>{{ $t('checkout.confirm') }}</template>
        </h1>
        <p class="text-xs text-gray-400">
          {{ $t('checkout.scannedSubtitle', { count: scannedItems.length }) }}
        </p>
      </div>

      <div class="flex gap-2">
        <!-- scan → action -->
        <button v-if="phase === 'scan' && scannedItems.length"
          @click="phase = 'action'"
          class="btn primary text-sm py-2 px-4 rounded-xl">
          {{ $t('checkout.nextArrow') }}
        </button>

        <!-- action/target/confirm → back -->
        <button v-if="phase !== 'scan'" :disabled="submitting" @click="goBack" class="btn ghost text-sm py-2 px-4 rounded-xl">
          {{ $t('checkout.back') }}
        </button>
      </div>
    </div>

    <BaseWorkflowSteps :steps="workflowSteps" :current="phase" />
    <BaseTaskHelp task="checkout" class="mx-4 mb-3" />

    <!-- ── PHASE: SCAN ────────────────────────────────────────────────── -->
    <template v-if="phase === 'scan'">

      <!-- Scan bar -->
      <div class="px-4 py-4">
        <div class="flex gap-2 items-center">
          <SearchField
            ref="scanFieldRef"
            v-model="scanQuery"
            :results="mappedLiveResults"
            :loading="scanLoading || liveLoading"
            :scan-mode="true"
            :camera-mode="true"
            :debounce="280"
            search-key="checkout-items"
            :placeholder="$t('projectItems.addSearchPlaceholder')"
            class="flex-1"
            @search="doLiveSearch"
            @scan="handleScan"
            @select="addFromLive($event.meta)"
          />
          <button
            @click="handleScan(scanQuery)"
            :disabled="!scanQuery || scanLoading"
            class="btn primary sm whitespace-nowrap"
          >
            {{ scanLoading ? '…' : $t('checkout.addButton') }}
          </button>
        </div>

        <Transition name="fade">
          <div v-if="scanError"
               class="mt-2 px-3 py-2 rounded-lg bg-red-900/40 border border-red-700/30 text-xs text-red-300 flex items-center gap-2">
            <MdiAlertCircle class="w-3.5 h-3.5 shrink-0" /> {{ scanError }}
            <button type="button" @click="scanError=null" class="ml-auto btn-icon danger" :aria-label="$t('common.dismiss')"><MdiClose class="w-4 h-4" /></button>
          </div>
        </Transition>
      </div>

      <!-- Scanned items list -->
      <div v-if="scannedItems.length" class="px-4 pb-2">
        <div class="flex items-center justify-between mb-2">
          <h2 class="text-xs font-semibold text-gray-400 uppercase tracking-wider">
            {{ $t('checkout.scannedLabel', { count: scannedItems.length }) }}
          </h2>
          <button @click="clearList" class="text-xs text-red-400 hover:text-red-300">{{ $t('common.clearAll') }}</button>
        </div>

        <ul class="space-y-2">
          <li v-for="m in scannedItems" :key="m.id"
              class="flex items-center gap-3 p-3 rounded-xl bg-[var(--color-surface)] border border-emerald-500/15">
            <!-- Bundle entry -->
            <template v-if="m._isBundle">
              <div class="w-7 h-7 rounded-full bg-emerald-900/60 border border-emerald-700/40 flex items-center justify-center shrink-0">
                <MdiPackageVariant class="w-3.5 h-3.5 text-emerald-300" />
              </div>
              <div class="flex-1 min-w-0">
                <span class="text-sm text-white truncate block">{{ m.name }}</span>
                <span class="text-xs text-gray-500">{{ m.sku }} · {{ $t('bundles.itemCount', { count: m.itemCount }) }}</span>
              </div>
            </template>
            <!-- Regular item entry -->
            <template v-else>
              <div class="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                   :class="m.is_checked_out || m.is_planned ? 'bg-amber-900/60 border border-amber-700/40' : 'bg-green-900/60 border border-green-700/40'">
                <svg v-if="!m.is_checked_out && !m.is_planned" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#86efac" stroke-width="3"><path d="m20 6-11 11-5-5"/></svg>
                <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" stroke-width="2"><path d="M12 9v4m0 4h.01M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Z"/></svg>
              </div>
              <div class="flex-1 min-w-0">
                <span class="text-sm text-white truncate block">{{ m.item?.name ?? `#${m.id}` }}</span>
                <span class="text-xs text-gray-500">
                  {{ typeLabel[m.item?.item_type] }}
                  <template v-if="m.qty"> · ×{{ m.qty }}</template>
                  <template v-if="m.serial_number"> · SN: {{ m.serial_number }}</template>
                </span>
                <span v-if="m.is_checked_out" class="text-[10px] text-red-400">{{ $t('checkout.checkedOutBadge') }}</span>
                <span v-else-if="m.is_planned" class="text-[10px] text-amber-400">{{ $t('checkout.plannedOnProject') }}</span>
              </div>
            </template>
            <button type="button" @click="removeItem(m.id)" class="btn-icon danger"
              :aria-label="$t('ux.removeScannedItem', { name: m._isBundle ? m.name : (m.item?.name ?? `#${m.id}`) })">
              <MdiClose class="w-4 h-4" />
            </button>
          </li>
        </ul>
      </div>

      <div v-else class="flex flex-col items-center justify-center py-16 text-center px-8">
        <MdiBarcodeScan class="w-16 h-16 text-gray-500 mb-4" />
        <p class="text-gray-300 font-medium">{{ $t('checkout.noItemsScanned') }}</p>
        <p class="text-gray-500 text-sm mt-1">{{ $t('checkout.noItemsScannedHint') }}</p>
      </div>
    </template>

    <!-- ── PHASE: ACTION ──────────────────────────────────────────────── -->
    <template v-else-if="phase === 'action'">
      <div class="px-4 py-4 space-y-3">
        <p class="text-xs text-gray-400 mb-1">
          {{ $t('checkout.chooseToDo', { count: scannedItems.length }) }}
        </p>

        <button
          v-for="action in ACTIONS"
          :key="action.id"
          @click="selectAction(action)"
          :disabled="action.disabled"
          class="w-full text-left p-4 rounded-2xl transition-all bg-[var(--color-surface)] border border-[var(--color-border)]"
          :class="action.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-[var(--color-surface-2)]'"
        >
          <div class="flex items-start gap-3">
            <component :is="action.icon" class="w-6 h-6 mt-0.5 shrink-0" :class="action.iconClass" />
            <div class="flex-1 min-w-0">
              <div class="font-semibold text-white text-sm flex items-center gap-2">
                {{ action.label }}
                <span v-if="action.disabled" class="text-xs font-normal text-gray-500 bg-white/5 px-2 py-0.5 rounded-full">{{ $t('checkout.soonBadge') }}</span>
              </div>
              <p class="text-xs text-gray-400 mt-0.5 leading-relaxed">{{ action.desc }}</p>
            </div>
            <svg v-if="!action.disabled" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="2" class="shrink-0 mt-1"><path d="m9 18 6-6-6-6"/></svg>
          </div>
        </button>
      </div>
    </template>

    <!-- ── PHASE: TARGET ──────────────────────────────────────────────── -->
    <template v-else-if="phase === 'target'">
      <div class="px-4 py-4 space-y-4">

        <!-- Selected action reminder -->
        <div class="flex items-center gap-2 p-3 rounded-xl text-sm bg-[var(--color-surface)] border border-[var(--color-border)]">
          <component :is="selectedAction?.icon" v-if="selectedAction?.icon" class="w-5 h-5 shrink-0" />
          <span class="text-gray-300">{{ selectedAction?.label }}</span>
        </div>

        <!-- ── checkout-person-project: step 1 = project, step 2 = person ─── -->
        <template v-if="selectedAction?.id === 'checkout-person-project'">

          <!-- STEP 1: Select project -->
          <template v-if="personOnProjectStep === 'project'">
            <p class="text-xs -mt-1" style="color:var(--color-muted)">
              {{ $t('checkout.projectBelongsTo', { entity: $t('entity.project') }) }}
            </p>

            <!-- Filter tabs -->
            <div class="flex gap-1 p-1 rounded-xl" style="background:var(--color-surface)">
              <button
                v-for="tab in PROJECT_TABS"
                :key="tab.key"
                @click="loadProjectTab(tab.key)"
                class="flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors"
                :class="projectFilterTab === tab.key
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'text-[var(--color-muted)] hover:text-[var(--color-text)]'"
              >
                {{ tab.label }}
              </button>
            </div>

            <!-- Search -->
            <div class="relative">
              <BaseFormField density="compact" class="!mb-0">
                <BaseInput
                  v-model="targetQuery"
                  type="text"
                  autocomplete="off"
                  :placeholder="$t('checkout.searchProject')"
                  :aria-label="$t('checkout.searchProject')"
                />
              </BaseFormField>
              <div v-if="projectTabLoading" class="absolute inset-y-0 right-3 flex items-center">
                <div class="w-4 h-4 border-2 border-gray-600 border-t-white rounded-full animate-spin"></div>
              </div>
            </div>

            <!-- Results -->
            <ul v-if="projectTabResults.length" class="space-y-1">
              <li
                v-for="p in projectTabResults"
                :key="p.id"
                @click="pickProjectForAssign(p)"
                class="flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-colors"
                style="background:var(--color-surface);border:1px solid var(--color-border);"
              >
                <div class="flex-1 min-w-0">
                  <span class="text-sm truncate block" style="color:var(--color-text)">{{ p.label }}</span>
                  <span v-if="p.sub" class="text-xs truncate block" style="color:var(--color-muted)">{{ p.sub }}</span>
                </div>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="2"><path d="m9 18 6-6-6-6"/></svg>
              </li>
            </ul>
            <p v-else-if="!projectTabLoading" class="text-xs text-center py-4" style="color:var(--color-muted)">
              {{ $t('checkout.noProjectsFound') }}
            </p>
          </template>

          <!-- STEP 2: Select person -->
          <template v-else>
            <!-- Selected project breadcrumb -->
            <div class="flex items-center gap-2 px-3 py-2 rounded-xl text-sm"
                 style="background:var(--color-surface);border:1px solid var(--color-border);">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#86efac" stroke-width="2.5"><path d="m20 6-11 11-5-5"/></svg>
              <span class="truncate" style="color:var(--color-text)">{{ selectedProjectForAssign?.label }}</span>
            </div>

            <!-- Person type toggle -->
            <div class="flex gap-1 p-1 rounded-xl" style="background:var(--color-surface)">
              <button
                @click="assignPersonType = 'user'; selectedTarget = null; targetQuery = ''"
                class="flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors"
                :class="assignPersonType === 'user'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'text-[var(--color-muted)] hover:text-[var(--color-text)]'"
              >
                {{ $t('checkout.systemUser') }}
              </button>
              <button
                @click="assignPersonType = 'anon'; selectedTarget = null; targetQuery = ''"
                class="flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors"
                :class="assignPersonType === 'anon'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'text-[var(--color-muted)] hover:text-[var(--color-text)]'"
              >
                {{ $t('checkout.guestAnon') }}
              </button>
            </div>

            <!-- User search -->
            <template v-if="assignPersonType === 'user'">
              <div class="relative">
                <BaseFormField density="compact" class="!mb-0">
                  <BaseInput
                    v-model="targetQuery"
                    type="text"
                    autocomplete="off"
                    :placeholder="$t('checkout.selectPersonPlaceholder')"
                    :aria-label="$t('checkout.selectPersonPlaceholder')"
                  />
                </BaseFormField>
                <div v-if="targetLoading" class="absolute inset-y-0 right-3 flex items-center">
                  <div class="w-4 h-4 border-2 border-gray-600 border-t-white rounded-full animate-spin"></div>
                </div>
              </div>
              <ul v-if="targetResults.length" class="space-y-1">
                <li
                  v-for="r in targetResults"
                  :key="r.id"
                  @click="pickTarget(r)"
                  class="flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer"
                  style="background:var(--color-surface);border:1px solid var(--color-border);"
                >
                  <div class="flex-1 min-w-0">
                    <span class="text-sm truncate block" style="color:var(--color-text)">{{ r.label }}</span>
                    <span v-if="r.sub" class="text-xs truncate block" style="color:var(--color-muted)">{{ r.sub }}</span>
                  </div>
                </li>
              </ul>
              <div v-if="selectedTarget"
                   class="flex items-center gap-3 p-4 rounded-xl"
                   style="background:var(--color-surface);border:1px solid rgba(16,185,129,0.3);">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#86efac" stroke-width="3"><path d="m20 6-11 11-5-5"/></svg>
                <span class="flex-1 text-sm truncate" style="color:var(--color-text)">{{ selectedTarget.label }}</span>
                <button @click="selectedTarget = null; targetQuery = ''" class="p-1" style="color:var(--color-muted)">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
                </button>
              </div>
              <button
                v-if="selectedTarget"
                @click="enterResolvePhase()"
                class="w-full btn primary py-4 rounded-2xl text-sm font-semibold">
                {{ $t('checkout.reviewConfirm') }}
              </button>
            </template>

            <!-- Anon guest form -->
            <template v-else>
              <div class="space-y-3">
                <BaseFormField :label="$t('checkout.fullName')" required>
                  <BaseInput v-model="anonUserForm.full_name" type="text" autocomplete="name" placeholder="Kari Nordmann" maxlength="200" />
                </BaseFormField>
                <div class="form-row">
                  <BaseFormField :label="$t('checkout.email')">
                    <BaseInput v-model="anonUserForm.email" type="email" placeholder="kari@eksempel.no" maxlength="254" />
                  </BaseFormField>
                  <BaseFormField :label="$t('checkout.phone')">
                    <BaseInput v-model="anonUserForm.phone" type="tel" placeholder="+47 999 00 000" maxlength="30" />
                  </BaseFormField>
                </div>
                <BaseFormField :label="$t('checkout.idBadge')">
                  <BaseInput v-model="anonUserForm.external_id" type="text" placeholder="BADGE-001" maxlength="100" />
                </BaseFormField>
              </div>
              <button
                v-if="anonUserFormValid"
                @click="enterResolvePhase()"
                class="w-full btn primary py-4 rounded-2xl text-sm font-semibold">
                {{ $t('checkout.reviewConfirm') }}
              </button>
            </template>
          </template>
        </template>

        <!-- ── Anon guest form ─────────────────────────────────────────── -->
        <template v-else-if="selectedAction?.target === 'anon'">
          <p class="text-xs text-gray-400 -mt-1">{{ $t('checkout.filledByBorrower') }}</p>

          <div class="space-y-3">
            <BaseFormField :label="$t('checkout.fullName')" required>
              <BaseInput
                v-model="anonUserForm.full_name"
                type="text"
                autocomplete="name"
                placeholder="Kari Nordmann"
                maxlength="200"
              />
            </BaseFormField>

            <div class="form-row">
              <BaseFormField :label="$t('checkout.email')">
                <BaseInput
                  v-model="anonUserForm.email"
                  type="email"
                  autocomplete="email"
                  placeholder="kari@eksempel.no"
                  maxlength="254"
                />
              </BaseFormField>
              <BaseFormField :label="$t('checkout.phone')">
                <BaseInput
                  v-model="anonUserForm.phone"
                  type="tel"
                  autocomplete="tel"
                  placeholder="+47 999 00 000"
                  maxlength="30"
                />
              </BaseFormField>
            </div>

            <div class="form-row">
              <BaseFormField :label="$t('checkout.organization')">
                <BaseInput
                  v-model="anonUserForm.organization"
                  type="text"
                  placeholder="Bedrift AS"
                  maxlength="200"
                />
              </BaseFormField>
              <BaseFormField :label="$t('checkout.idBadge')" :hint="$t('checkout.idBadgeHint')">
                <BaseInput
                  v-model="anonUserForm.external_id"
                  type="text"
                  placeholder="BADGE-001"
                  maxlength="100"
                />
              </BaseFormField>
            </div>

            <BaseFormField :label="$t('checkout.notesLabel')">
              <BaseTextarea
                v-model="anonUserForm.notes"
                :rows="2"
                :placeholder="$t('checkout.notesPlaceholder')"
              />
            </BaseFormField>
          </div>

          <button
            v-if="anonUserFormValid"
            @click="enterResolvePhase()"
            class="w-full btn primary py-4 rounded-2xl text-sm font-semibold">
            {{ $t('checkout.reviewConfirm') }}
          </button>
        </template>

        <!-- ── Project / person search ────────────────────────────────── -->
        <template v-else>
          <!-- Search input -->
          <BaseFormField
            :label="selectedAction?.target === 'project' ? $t('checkout.searchProjects', { entity: $t('entity.project') }) : $t('checkout.searchPeople')"
          >
            <div class="relative">
              <BaseInput
                v-model="targetQuery"
                type="text"
                autocomplete="off"
                autocorrect="off"
                spellcheck="false"
                :placeholder="selectedAction?.target === 'project' ? $t('checkout.selectProjectPlaceholder', { entity: $t('entity.project') }) : $t('checkout.selectPersonPlaceholder')"
              />
              <div v-if="targetLoading" class="absolute inset-y-0 right-3 flex items-center">
                <div class="w-4 h-4 border-2 border-gray-600 border-t-white rounded-full animate-spin"></div>
              </div>
            </div>
          </BaseFormField>

          <!-- Results -->
          <ul v-if="targetResults.length" class="space-y-1">
            <li
              v-for="r in targetResults"
              :key="r.id"
              @click="pickTarget(r)"
              class="flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-colors bg-[var(--color-surface)] border border-[var(--color-border)]"
            >
              <div class="flex-1 min-w-0">
                <span class="text-sm text-white truncate block">{{ r.label }}</span>
                <span v-if="r.sub" class="text-xs text-gray-500 truncate block">{{ r.sub }}</span>
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="2"><path d="m9 18 6-6-6-6"/></svg>
            </li>
          </ul>

          <!-- Selected target confirmation -->
          <div v-if="selectedTarget"
               class="flex items-center gap-3 p-4 rounded-xl bg-[var(--color-surface)] border border-emerald-500/30">
            <div class="w-8 h-8 rounded-full bg-green-900/60 border border-green-700/40 flex items-center justify-center shrink-0">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#86efac" stroke-width="3"><path d="m20 6-11 11-5-5"/></svg>
            </div>
            <div class="flex-1 min-w-0">
              <span class="text-sm text-white truncate block">{{ selectedTarget.label }}</span>
              <span v-if="selectedTarget.sub" class="text-xs text-gray-500">{{ selectedTarget.sub }}</span>
            </div>
            <button type="button" @click="selectedTarget = null; targetQuery = ''" class="btn-icon danger" :aria-label="$t('common.clear')">
              <MdiClose class="w-4 h-4" />
            </button>
          </div>

          <!-- Proceed to confirm -->
          <button
            v-if="selectedTarget"
            @click="enterResolvePhase()"
            class="w-full btn primary py-4 rounded-2xl text-sm font-semibold">
            {{ $t('checkout.reviewConfirm') }}
          </button>
        </template>
      </div>
    </template>

    <!-- ── PHASE: RESOLVE ──────────────────────────────────────────────── -->
    <template v-else-if="phase === 'resolve'">
      <div class="px-4 py-4 space-y-5">
        <p class="text-xs text-gray-400">
          {{ $t('checkout.bundleResolveTip') }}
        </p>

        <!-- Loading state -->
        <div v-if="resolveLoading" class="flex items-center justify-center py-8">
          <div class="w-5 h-5 border-2 border-gray-600 border-t-white rounded-full animate-spin"></div>
          <span class="ml-3 text-sm text-gray-400">{{ $t('checkout.loadingBundleDetails') }}</span>
        </div>

        <!-- Per-bundle groups -->
        <template v-else>
          <div
            v-for="(group, gi) in resolveGroups"
            :key="group.bundleId"
            class="rounded-2xl p-4 space-y-3 bg-[var(--color-surface)] border border-[var(--color-border)]"
          >
            <div class="flex items-center gap-2">
              <MdiPackageVariant class="w-4 h-4 text-emerald-400 shrink-0" />
              <h3 class="text-sm font-semibold text-white truncate">{{ group.bundleName }}</h3>
            </div>

            <!-- Each member needing resolution -->
            <div
              v-for="(member, mi) in group.members"
              :key="member.bundleItemId"
              class="space-y-2 pl-2 border-l-2"
              :style="member.selectedInstanceId ? 'border-color:rgba(16,185,129,0.4)' : 'border-color:rgba(255,255,255,0.08)'"
            >
              <div class="flex items-center gap-2">
                <span class="text-sm text-white">{{ member.itemName }}</span>
                <span v-if="member.quantity > 1" class="text-xs text-gray-500">×{{ member.quantity }}</span>
              </div>

              <!-- Instance search/filter -->
              <BaseFormField density="compact" class="!mb-0">
                <BaseInput
                  v-model="member.searchQuery"
                  type="text"
                  autocomplete="off"
                  density="compact"
                  :placeholder="$t('checkout.searchInstances')"
                  :aria-label="$t('checkout.searchInstances')"
                />
              </BaseFormField>

              <!-- Instance list -->
              <ul class="space-y-1 max-h-40 overflow-y-auto">
                <li
                  v-for="inst in member.instances.filter(i =>
                    !member.searchQuery || i.label.toLowerCase().includes(member.searchQuery.toLowerCase())
                  )"
                  :key="inst.id"
                  @click="selectInstance(gi, mi, inst.id)"
                  class="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors text-xs"
                  :class="inst.id === member.selectedInstanceId
                    ? 'bg-emerald-500/15 border border-emerald-500/30'
                    : 'bg-[var(--color-surface-2)] border border-white/5'"
                >
                  <div class="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                       :class="inst.id === member.selectedInstanceId
                         ? 'bg-emerald-500/30'
                         : 'bg-white/[0.06]'">
                    <svg v-if="inst.id === member.selectedInstanceId" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#86efac" stroke-width="3"><path d="m20 6-11 11-5-5"/></svg>
                  </div>
                  <span class="flex-1 truncate" :class="inst.id === member.selectedInstanceId ? 'text-white' : 'text-gray-300'">{{ inst.label }}</span>
                  <span
                    class="text-[10px] px-1.5 py-0.5 rounded"
                    :class="inst.available ? 'bg-green-900/40 text-green-300' : 'bg-red-900/40 text-red-400'"
                  >{{ inst.sub }}</span>
                </li>
                <li v-if="member.instances.filter(i =>
                    !member.searchQuery || i.label.toLowerCase().includes(member.searchQuery.toLowerCase())
                  ).length === 0" class="text-xs text-gray-500 px-3 py-2">
                  {{ $t('checkout.noMatchingInstances') }}
                </li>
              </ul>

              <!-- Selected indicator -->
              <div v-if="member.selectedInstanceId" class="flex items-center gap-2 text-xs text-emerald-300">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="m20 6-11 11-5-5"/></svg>
                {{ member.instances.find(i => i.id === member.selectedInstanceId)?.label }}
              </div>
            </div>
          </div>

          <!-- Proceed -->
          <button
            :disabled="!allResolved"
            @click="refreshAvailability().then(() => { phase = 'confirm' })"
            class="w-full btn primary py-4 rounded-2xl text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
            {{ allResolved ? $t('checkout.reviewConfirm') : $t('checkout.resolveInstances') }}
          </button>
        </template>
      </div>
    </template>

    <!-- ── PHASE: CONFIRM ─────────────────────────────────────────────── -->
    <template v-else-if="phase === 'confirm'">
      <div class="px-4 py-4 space-y-4">

        <!-- Summary card -->
        <div class="card rounded-2xl p-4 space-y-3">
          <h3 class="text-sm font-semibold text-white">{{ $t('checkout.summary') }}</h3>

          <div class="flex items-center gap-2 text-sm">
            <span class="text-gray-400 w-20 shrink-0">{{ $t('checkout.action') }}</span>
            <span class="text-white flex items-center gap-1.5">
              <component :is="selectedAction?.icon" v-if="selectedAction?.icon" class="w-4 h-4 shrink-0" />
              {{ selectedAction?.label }}
            </span>
          </div>

          <div v-if="selectedAction?.id !== 'maintenance'" class="flex items-center gap-2 text-sm">
            <span class="text-gray-400 w-20 shrink-0">
              {{ selectedAction?.id === 'checkout-person-project' ? $t('entity.project') : selectedAction?.target === 'project' ? $t('entity.project') : selectedAction?.target === 'anon' ? $t('checkout.guest') : $t('checkout.person') }}
            </span>
            <span v-if="selectedAction?.target === 'anon'" class="text-white min-w-0">
              <span class="block truncate">{{ anonUserForm.full_name }}</span>
              <span v-if="anonUserForm.organization" class="block text-xs text-gray-400 truncate">{{ anonUserForm.organization }}</span>
              <span v-if="anonUserForm.email" class="block text-xs text-gray-500 truncate">{{ anonUserForm.email }}</span>
              <span v-if="anonUserForm.phone" class="block text-xs text-gray-500 truncate">{{ anonUserForm.phone }}</span>
              <span v-if="anonUserForm.external_id" class="block text-xs text-gray-500 truncate">ID: {{ anonUserForm.external_id }}</span>
            </span>
            <span v-else-if="selectedAction?.id === 'checkout-person-project'" class="text-white truncate">{{ selectedProjectForAssign?.label }}</span>
            <span v-else class="text-white truncate">{{ selectedTarget?.label }}</span>
          </div>

          <div v-if="selectedAction?.id === 'checkout-person-project'" class="flex items-center gap-2 text-sm">
            <span class="text-gray-400 w-20 shrink-0">{{ $t('checkout.person') }}</span>
            <span v-if="assignPersonType === 'anon'" class="text-white truncate">{{ anonUserForm.full_name }}</span>
            <span v-else class="text-white truncate">{{ selectedTarget?.label }}</span>
          </div>

          <div class="flex items-start gap-2 text-sm pt-1 border-t border-white/5">
            <span class="text-gray-400 w-20 shrink-0 mt-0.5">{{ $t('checkout.items', { count: scannedItems.length }) }}</span>
            <ul class="flex-1 space-y-1">
              <li v-for="m in scannedItems" :key="m.id" class="text-white text-xs flex items-center gap-1.5">
                <template v-if="m._isBundle">
                  <MdiPackageVariant class="w-3 h-3 text-emerald-400 shrink-0" />
                  <span class="truncate">{{ m.name }}</span>
                  <span class="text-gray-500 shrink-0">{{ m.itemCount }} {{ $t('ui.items') }}</span>
                </template>
                <template v-else>
                  <span class="text-gray-600 font-mono text-[10px]">{{ typeLabel[m.item?.item_type] ?? '?' }}</span>
                  <span class="truncate">{{ m.item?.name ?? `#${m.id}` }}</span>
                  <span v-if="m.qty" class="text-gray-500 shrink-0">×{{ m.qty }}</span>
                  <span v-else-if="m.serial_number" class="text-gray-500 shrink-0">{{ m.serial_number }}</span>
                  <span v-if="m.is_checked_out" class="text-[9px] px-1 py-0.5 rounded bg-red-900/40 text-red-400 shrink-0">{{ $t('checkout.checkedOutBadge') }}</span>
                  <span v-else-if="m.is_planned" class="text-[9px] px-1 py-0.5 rounded bg-amber-900/40 text-amber-400 shrink-0">{{ $t('checkout.plannedBadge') }}</span>
                </template>
              </li>
            </ul>
          </div>
        </div>

        <!-- Availability warnings -->
        <div v-if="confirmWarnings.length" class="rounded-xl p-3 space-y-2"
             style="background:rgba(245,158,11,0.08);border:1px solid rgba(245,158,11,0.25);">
          <div class="flex items-center gap-2 text-amber-400 text-xs font-semibold uppercase tracking-wider">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 9v4m0 4h.01M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Z"/></svg>
            {{ $t('checkout.availabilityWarnings') }}
          </div>
          <ul class="space-y-1">
            <li v-for="w in confirmWarnings" :key="w.id" class="text-xs flex items-center gap-1.5"
                :class="w.level === 'error' ? 'text-red-400' : 'text-amber-300'">
              <span class="w-1.5 h-1.5 rounded-full shrink-0" :class="w.level === 'error' ? 'bg-red-500' : 'bg-amber-500'" />
              {{ w.msg }}
            </li>
          </ul>
          <p class="text-[10px] text-gray-500">{{ $t('checkout.warningMsg') }}</p>
        </div>

        <!-- Expected check-in (person and guest checkout) -->
        <div v-if="selectedAction?.id === 'checkout-person' || selectedAction?.id === 'checkout-anon' || selectedAction?.id === 'checkout-person-project'" class="space-y-2">
          <!-- Quick-pick presets -->
          <div class="flex gap-2 flex-wrap">
            <button
              v-for="q in QUICK_DATES"
              :key="q.key"
              type="button"
              @click="setQuickDate(q.key)"
              class="px-3 py-1.5 rounded-lg text-xs font-medium transition-all border"
              :style="activeQuickDate === q.key
                ? 'background:var(--color-primary);border-color:var(--color-primary);color:#fff;'
                : 'background:var(--color-surface);border-color:var(--color-border);color:var(--color-muted);'"
            >{{ q.label }}</button>
            <button
              v-if="expectedCheckin"
              type="button"
              @click="expectedCheckin = ''"
              class="px-3 py-1.5 rounded-lg text-xs font-medium transition-all border"
              style="background:transparent;border-color:var(--color-border);color:var(--color-muted);"
            >{{ $t('common.clear') }}</button>
          </div>

          <BaseFormField :label="$t('checkout.expectedReturn')" :hint="$t('checkout.optionalDate')">
            <BaseInput
              v-model="expectedCheckin"
              type="datetime-local"
            />
          </BaseFormField>
        </div>

        <!-- Errors -->
        <div v-if="submitErrors.length" class="p-3 rounded-xl bg-red-900/20 border border-red-700/30 text-xs text-red-300 space-y-1">
          <p v-for="(e, i) in submitErrors" :key="i" class="flex items-center gap-1"><MdiAlertCircle class="w-3.5 h-3.5 shrink-0" /> {{ e }}</p>
        </div>

        <!-- Maintenance note -->
        <div v-if="selectedAction?.id === 'maintenance'" class="space-y-2">
          <BaseFormField :label="$t('checkout.maintenanceNote')">
            <BaseTextarea
              id="maint-note"
              v-model="maintenanceNote"
              :rows="3"
              :placeholder="$t('checkout.maintenanceNotePlaceholder')"
            />
          </BaseFormField>
          <p class="text-xs text-amber-300">{{ $t('checkout.maintenanceConfirmHint') }}</p>
        </div>

        <!-- Warning for instantly-active actions -->
        <div v-if="selectedAction?.id !== 'pick-list' && selectedAction?.id !== 'maintenance' && !suppressCheckoutWarning"
             class="flex gap-2 p-3 rounded-xl bg-yellow-900/20 border border-yellow-700/30 text-xs text-yellow-300">
          <MdiAlertCircle class="w-3.5 h-3.5 shrink-0" />
            <span class="flex-1" v-html="$t('checkout.willBeMarked')"></span>
            <button @click="toggleSuppressWarning" class="shrink-0 text-yellow-500 hover:text-yellow-300 text-xs underline whitespace-nowrap">{{ $t('checkout.dontShow') }}</button>
          </div>
          <div v-else-if="selectedAction?.id !== 'pick-list' && selectedAction?.id !== 'maintenance' && suppressCheckoutWarning"
               class="flex items-center gap-2 text-xs text-gray-500">
            <span>{{ $t('checkout.checkoutWarningHidden') }}</span>
            <button @click="toggleSuppressWarning" class="underline hover:text-gray-300">{{ $t('checkout.showAgain') }}</button>
          </div>

        <button
          v-if="selectedAction?.id === 'maintenance'"
          @click="submitMaintenance"
          :disabled="maintenanceSubmitting"
          class="w-full btn primary py-4 rounded-2xl text-base font-semibold tracking-wide">
          {{ maintenanceSubmitting ? $t('common.processing') : $t('checkout.confirmMaintenance', { count: scannedItems.length }) }}
        </button>
        <button
          v-else
          @click="handleSubmit"
          :disabled="submitting || (selectedAction?.target === 'anon' && !anonUserFormValid)"
          class="w-full btn primary py-4 rounded-2xl text-base font-semibold tracking-wide">
          {{ submitting ? $t('common.processing') : $t('checkout.confirmBtn', { count: scannedItems.length }) }}
        </button>
      </div>
    </template>

    <!-- ── QUANTITY MODAL ─────────────────────────────────────────────── -->
    <QuantityModal
      :item="qtyTarget"
      mode="checkout"
      :stockInfo="qtyTarget?.item?.item_type === 4 ? qtyStockInfo : null"
      @confirm="onQtyConfirm"
      @cancel="onQtyCancel"
    />

  </div>
</template>

<style>
.fade-enter-active, .fade-leave-active { transition: opacity .15s ease; }
.fade-enter-from, .fade-leave-to       { opacity: 0; }
</style>
